import { supabase } from './supabase';

export type SiteActivityRow = {
  event: string;
  timestamp: string;
  app: string | null;
  path: string | null;
  detail: string | null;
};

export type SubscriberSiteActivityResult = {
  events: SiteActivityRow[];
  subscriberId: string;
  lookbackDays: number;
  posthogPersonUrl: string | null;
};

async function edgeFunctionErrorMessage(
  error: { message?: string; context?: unknown },
  data: { error?: string } | null | undefined,
  fallback: string,
): Promise<string> {
  const ctx = error.context;
  if (ctx && typeof (ctx as Response).json === 'function') {
    try {
      const parsed = (await (ctx as Response).clone().json()) as { error?: string };
      if (parsed?.error) return parsed.error;
    } catch {
      try {
        const text = await (ctx as Response).clone().text();
        if (text.trim()) return text;
      } catch {
        /* ignore */
      }
    }
  }
  return data?.error || error.message || fallback;
}

export async function loadSubscriberSiteActivity(
  subscriberId: string,
): Promise<SubscriberSiteActivityResult> {
  const { data, error } = await supabase.functions.invoke<SubscriberSiteActivityResult & {
    error?: string;
  }>('posthog-subscriber-activity', {
    body: { subscriberId },
  });

  if (error) {
    throw new Error(
      await edgeFunctionErrorMessage(error, data, 'Failed to load analytics'),
    );
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  if (!data?.events) {
    throw new Error('Unexpected response from posthog-subscriber-activity');
  }

  return data;
}

export function formatSiteActivityWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const EVENT_LABELS: Record<string, string> = {
  page_viewed: 'Page view',
  gated_content_viewed: 'Gated (teaser)',
  gated_access_requested: 'Requested access',
  newsletter_subscribe_completed: 'Subscribed',
  auth_callback_completed: 'Signed in',
  booking_form_submitted: 'Booking started',
  booking_completed: 'Booked meeting',
  $pageview: 'Page view',
};

export function siteActivityEventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event.replace(/_/g, ' ');
}
